import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../ui/LoadingSpinner';

const ProtectedRoute = ({ 
  children, 
  requireAuth = true,
  requiredPermission = null,
  requiredRole = null,
  fallback = null
}) => {
  const { user, isLoading, hasPermission, isAdmin } = useAuth();

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="protected-route-loading">
        <LoadingSpinner size="large" />
        <p>Checking access...</p>
      </div>
    );
  }

  // If authentication is required but user is not authenticated
  if (requireAuth && !user) {
    // This should not happen with our Authenticator wrapper, but adding as safeguard
    return (
      <div className="access-denied">
        <h2>Authentication Required</h2>
        <p>Please sign in to access this page.</p>
      </div>
    );
  }

  // If specific role is required
  if (requiredRole && user?.role !== requiredRole) {
    return fallback || (
      <div className="access-denied">
        <div className="access-denied-icon">🚫</div>
        <h2>Access Denied</h2>
        <p>
          This page requires {requiredRole.toLowerCase()} privileges. 
          Your account type is {user?.role?.toLowerCase() || 'unknown'}.
        </p>
        <p>Please contact an administrator if you believe this is an error.</p>
      </div>
    );
  }

  // If specific permission is required
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return fallback || (
      <div className="access-denied">
        <div className="access-denied-icon">🚫</div>
        <h2>Insufficient Permissions</h2>
        <p>You don't have the required permissions to access this page.</p>
        <p>Required permission: <code>{requiredPermission}</code></p>
        <p>Please contact an administrator if you believe this is an error.</p>
      </div>
    );
  }

  // If admin is required (convenience check)
  if (requiredRole === 'ADMIN' && !isAdmin()) {
    return fallback || (
      <div className="access-denied">
        <div className="access-denied-icon">👨‍💼</div>
        <h2>Admin Access Required</h2>
        <p>This page is only accessible to administrators.</p>
        <p>Your current role: {user?.role || 'Unknown'}</p>
      </div>
    );
  }

  // All checks passed, render the protected content
  return children;
};

export default ProtectedRoute;